import React, { useEffect, useState, useRef } from 'react';

import Tippy from '@tippyjs/react';
import {
  Edit2,
  Trash,
  X,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  EllipsisVertical,
  SaveIcon,
} from 'lucide-react';

import Loader from './Loader';
import Pagination from './Pagination';
import { confirmAction } from '../../utils/sweetAlertUtils';
import { defaultExpandedRowRenderer } from '../../utils/tableUtils';

import 'tippy.js/dist/tippy.css';

export interface TableColumn {
  label: string | React.ReactNode;
  key: string;
  format?: (value: any, row: any) => string;
  isImage?: boolean;
  imageSize?: string;
  editable?: boolean;
  width?: string;
  render?: (row: any) => React.ReactElement;
  className?: string;
  editTemplate?: (
    value: any,
    onChange: (val: any) => void,
    row: any,
  ) => React.ReactElement;
  priority?: number; // Column priority for dynamic hiding: lower number = higher importance (1 = always visible, 5 = default, 8+ = hide first)
  sortable?: boolean; // Can this column be sorted?
  sortKey?: string; // Backend field name for sorting (e.g., "FirstName")
  showInReview?: boolean; // Include this column in the FileViewer review side panel
}

export interface TableAction {
  label: string;
  onClick: (row: any) => void;
  confirmMessage?: string;
  icon?: React.ReactNode;
  className?: string;
}

interface TableProps<T = any> {
  columns: TableColumn[];
  data: any[];
  actions?: {
    onEdit?: (row: T) => void;
    onDelete?: (row: T) => void;
    extra?: TableAction[] | ((row: T) => TableAction[]);
  };
  groupBy?: string[]; // New prop for grouping data
  groupHeaderRender?: (group: string, rows: any[]) => React.ReactElement; // Custom group header render
  allowPaging?: boolean;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  isLoading: boolean;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  noDataMessage?: string;
  enableInlineEditing?: boolean;
  triggerNewRowEdit?: boolean;
  setTriggerNewRowEdit?: React.Dispatch<React.SetStateAction<boolean>>;
  expandableDetails?: boolean; // Enable expandable row details
  renderExpandedRow?: (row: any) => React.ReactElement; // Custom expanded row render
  sortBy?: string; // Current sort field
  sortDescending?: boolean; // Sort direction
  onSort?: (sortKey: string) => void; // Callback when column clicked
}

const Table: React.FC<TableProps> = ({
  columns,
  data,
  actions,
  groupBy,
  groupHeaderRender, // Custom group header render prop
  allowPaging = true,
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  onPageChange = () => { },
  onPageSizeChange = () => { },
  isLoading,
  noDataMessage = 'No records found',
  enableInlineEditing = false,
  triggerNewRowEdit = false,
  setTriggerNewRowEdit,
  expandableDetails = true,
  renderExpandedRow,
  sortBy,
  sortDescending = true,
  onSort,
}) => {
  // State for dynamically hidden columns
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});
  const [rowEditIndex, setRowEditIndex] = useState<number | null>(null);
  const [editedRow, setEditedRow] = useState<any>({});
  const [dropdownVisible, setDropdownVisible] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number | string>>(
    new Set(),
  );
  const [shouldShowExpand, setShouldShowExpand] = useState(false);
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLTableElement>(null);
  const headerRowRef = useRef<HTMLTableRowElement>(null);

  // Use default expanded row renderer if none provided, but only show hidden columns
  const expandedRowRenderer = renderExpandedRow || ((row: any) => {
    const hiddenColumns = columns.filter(col => hiddenColumnKeys.has(col.key));
    return defaultExpandedRowRenderer(hiddenColumns, row);
  });

  // Dynamic column hiding based on actual available space
  useEffect(() => {
    if (!expandableDetails || !tableRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (!tableRef.current || !headerRowRef.current) return;

      const tableWidth = tableRef.current.offsetWidth;
      const cells = Array.from(headerRowRef.current.children) as HTMLElement[];

      // Minimum column width before it becomes unreadable (text can't wrap anymore)
      const MIN_COLUMN_WIDTH = 100; // px - minimum before content becomes unusable

      // Get columns with their priorities (lower number = higher priority to keep visible)
      const columnsWithPriority = columns.map((col, index) => {
        let priority = col.priority;

        // Auto-assign priorities if not set
        if (priority === undefined) {
          const key = col.key.toLowerCase();
          if (key === 'member' || key === 'title') {
            priority = 1; // Always visible
          } else if (key === 'createdutc' || key === 'updatedutc') {
            priority = 8; // Hide first
          } else {
            priority = 5; // Medium priority
          }
        }

        return { col, index, priority, cell: cells[index + (shouldShowExpand ? 1 : 0)] };
      }).filter(item => item.cell); // Filter out undefined cells

      // Calculate minimum required width (all columns at minimum size)
      const actionColumnWidth = 80;
      const minRequiredWidth = (columnsWithPriority.length * MIN_COLUMN_WIDTH) + actionColumnWidth;

      // Only hide columns if table is genuinely too narrow for minimum widths
      const newHiddenKeys = new Set<string>();

      if (tableWidth < minRequiredWidth) {
        // Need to hide some columns - prioritize by priority number
        const sortedByPriority = [...columnsWithPriority].sort((a, b) => b.priority - a.priority);

        let availableWidth = tableWidth - actionColumnWidth;
        const visibleCols: typeof columnsWithPriority = [];

        // Keep highest priority columns (lowest number) that fit at minimum width
        for (const item of [...columnsWithPriority].sort((a, b) => a.priority - b.priority)) {
          if (availableWidth >= MIN_COLUMN_WIDTH || item.priority === 1) {
            visibleCols.push(item);
            availableWidth -= MIN_COLUMN_WIDTH;
          } else {
            newHiddenKeys.add(item.col.key);
          }
        }
      }
      // else: all columns fit, let them wrap naturally - don't hide anything

      // Update hidden columns state
      setHiddenColumnKeys(newHiddenKeys);
      setShouldShowExpand(newHiddenKeys.size > 0);
    });

    resizeObserver.observe(tableRef.current);

    // Initial check
    setTimeout(() => {
      if (tableRef.current) {
        resizeObserver.observe(tableRef.current);
      }
    }, 100);

    return () => resizeObserver.disconnect();
  }, [columns, expandableDetails, data]);

  useEffect(() => {
    if (enableInlineEditing && data.length > 0 && triggerNewRowEdit) {
      const lastRow = data[data.length - 1];
      setRowEditIndex(lastRow.id || data.length - 1);
      setEditedRow(lastRow);
      setTriggerNewRowEdit?.(false);
    }
  }, [data, triggerNewRowEdit]);

  // Group data by specified fields
  const groupedData = groupBy
    ? data.reduce(
      (acc, row) => {
        const groupKey = groupBy.map((key) => row[key]).join(' - ');
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(row);

        return acc;
      },
      {} as Record<string, any[]>,
    )
    : { '': data };

  const toggleGroup = (group: string): void => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const toggleDropdown = (index: number): void => {
    setDropdownVisible(dropdownVisible === index ? null : index);
  };

  const closeDropdown = (): void => {
    setDropdownVisible(null);
  };

  const toggleExpandedRow = (rowId: number | string): void => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const getResponsiveClass = (col: TableColumn): string => {
    // Check if this column is dynamically hidden
    return hiddenColumnKeys.has(col.key) ? 'hidden' : '';
  };

  const startEditing = (idx: number, row: any): void => {
    if (enableInlineEditing) {
      setRowEditIndex(row.id || idx);
      setEditedRow({ ...row });
    } else if (actions && actions.onEdit) {
      actions.onEdit(row);
    }
  };

  const cancelEditing = (): void => {
    setRowEditIndex(null);
    setEditedRow({});
  };

  const saveEdit = (): void => {
    if (actions && actions.onEdit) {
      actions.onEdit(editedRow);
    }
    setRowEditIndex(null);
    setEditedRow({});
  };

  const handleEditChange = (key: string, value: any): void => {
    setEditedRow((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSort = (column: TableColumn): void => {
    if (!column.sortable || !onSort) return;
    const sortKey = column.sortKey || column.key;
    onSort(sortKey);
  };

  const getSortIcon = (column: TableColumn): React.ReactNode => {
    if (!column.sortable) return null;
    const sortKey = column.sortKey || column.key;
    if (sortBy !== sortKey) return null;
    return sortDescending ? (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    );
  };

  return (
    <div className="w-full col-span-full">
      <table ref={tableRef} className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700">
        <thead>
          <tr ref={headerRowRef}>
            {shouldShowExpand && (
              <th className="px-2 py-2 border-b border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 w-12">
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                style={{ width: col.width }}
                onClick={() => handleSort(col)}
                className={`px-4 py-2 border-b border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 ${getResponsiveClass(col)} ${col.className || ''} ${col.sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none' : ''}`}
              >
                <div className="flex items-center">
                  {col.label}
                  {getSortIcon(col)}
                </div>
              </th>
            ))}
            {actions &&
              (actions.onEdit || actions.onDelete || actions.extra) && (
                <th className="px-2 py-2 border-b border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-700 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 w-24">
                  Actions
                </th>
              )}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td
                colSpan={columns.length + (actions ? 1 : 0) + (shouldShowExpand ? 1 : 0)}
                className="text-center py-4"
              >
                <Loader />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (actions ? 1 : 0) + (shouldShowExpand ? 1 : 0)}
                className="text-center py-4 text-sm text-gray-500 dark:text-gray-400"
              >
                {noDataMessage}
              </td>
            </tr>
          ) : (
            Object.entries(groupedData).map(([group, rows]) => {
              const typedRows = rows as any[]; // Explicit type assertion

              return (
                <React.Fragment key={group}>
                  {/* Group Header */}
                  {group && (
                    <tr
                      className="bg-gray-100 dark:bg-gray-700 cursor-pointer"
                      onClick={() => toggleGroup(group)}
                    >
                      <td
                        colSpan={columns.length + (actions ? 1 : 0) + (shouldShowExpand ? 1 : 0)}
                        className="px-0 py-2 font-semibold text-gray-700 dark:text-gray-300"
                      >
                        <div className="px-4 flex items-center">
                          {/* Left Section: Chevron */}
                          <span className="mr-2">
                            {collapsedGroups[group] ? (
                              <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                            )}
                          </span>

                          {/* Right Section: Full Flex Container */}
                          <div className="flex flex-1 justify-between items-center">
                            {/* Inject Custom Header */}
                            {groupHeaderRender &&
                              groupHeaderRender(group, typedRows)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* Group Rows */}
                  {!collapsedGroups[group] &&
                    typedRows.map((row, idx) => {
                      const rowId = row.id || idx;
                      const isExpanded = expandedRows.has(rowId);

                      return (
                        <React.Fragment key={rowId}>
                          <tr className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                            {shouldShowExpand && (
                              <td className="px-2 py-2 border-b border-gray-300 text-xs text-gray-700 dark:border-gray-700 dark:text-gray-300">
                                <button
                                  onClick={() => toggleExpandedRow(rowId)}
                                  className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                                  aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-5 h-5" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5" />
                                  )}
                                </button>
                              </td>
                            )}
                            {columns.map((col) => (
                              <td
                                key={col.key}
                                style={{ width: col.width }}
                                className={`px-4 py-2 border-b border-gray-300 text-xs text-gray-700 dark:border-gray-700  dark:text-gray-300 ${getResponsiveClass(col)} ${col.className || ''}`}
                              >
                                {rowEditIndex === (row.id || idx) &&
                                  col.editable &&
                                  enableInlineEditing ? (
                                  col.editTemplate ? (
                                    col.editTemplate(
                                      editedRow[col.key],
                                      (val) => handleEditChange(col.key, val),
                                      row,
                                    )
                                  ) : (
                                    <input
                                      type="text"
                                      value={editedRow[col.key] || ''}
                                      onChange={(e) =>
                                        handleEditChange(col.key, e.target.value)
                                      }
                                      className="border px-2 py-1 w-full bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                    />
                                  )
                                ) : col.render ? (
                                  col.render(row)
                                ) : col.isImage ? (
                                  row[col.key] ? (
                                    <img
                                      src={row[col.key]}
                                      alt=""
                                      className="rounded-full"
                                      style={{
                                        width: col.imageSize || '40px',
                                        height: col.imageSize || '40px',
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="rounded-full bg-gray-300 dark:bg-gray-600 text-white flex items-center justify-center"
                                      style={{
                                        width: col.imageSize || '40px',
                                        height: col.imageSize || '40px',
                                      }}
                                    >
                                      ?
                                    </div>
                                  )
                                ) : col.format ? (
                                  col.format(row[col.key], row)
                                ) : (
                                  row[col.key]
                                )}
                              </td>
                            ))}
                            {actions &&
                              (actions.onEdit ||
                                actions.onDelete ||
                                actions.extra) && (
                                <td className="px-4 py-2 border-b border-gray-300 text-sm text-left dark:border-gray-700">
                                  <div className="flex space-x-2 justify-start">
                                    {enableInlineEditing &&
                                      rowEditIndex === (row.id || idx) ? (
                                      <>
                                        <Tippy content="Save">
                                          <button
                                            type="button"
                                            onClick={saveEdit}
                                            className="text-white bg-green-500 p-2 hover:bg-green-600 transition-colors cursor-pointer"
                                          >
                                            <SaveIcon className="h-4 w-4" />
                                          </button>
                                        </Tippy>
                                        <Tippy content="Cancel">
                                          <button
                                            type="button"
                                            onClick={cancelEditing}
                                            className="text-white bg-gray-500 p-2 hover:bg-gray-600 transition-colors cursor-pointer"
                                          >
                                            <X className="h-4 w-4" />
                                          </button>
                                        </Tippy>
                                      </>
                                    ) : (
                                      <>
                                        {actions.onEdit && !row.isDeleted && (
                                          <Tippy content="Edit">
                                            <button
                                              type="button"
                                              onClick={() => startEditing(idx, row)}
                                              className="text-white bg-primary p-2 hover:bg-opacity-90 transition-colors cursor-pointer"
                                            >
                                              <Edit2 className="h-4 w-4" />
                                            </button>
                                          </Tippy>
                                        )}
                                        {actions.onDelete && (
                                          <Tippy content="Delete">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                actions.onDelete?.(row)
                                              }
                                              className="text-white bg-red-500 p-2 hover:bg-red-600 transition-colors cursor-pointer"
                                            >
                                              <Trash className="h-4 w-4" />
                                            </button>
                                          </Tippy>
                                        )}
                                        {actions.extra && (() => {
                                          const extraActions =
                                            typeof actions.extra === 'function'
                                              ? actions.extra(row)
                                              : actions.extra;

                                          if (!extraActions || extraActions.length === 0) {
                                            return null;
                                          }

                                          return (
                                            <div className="relative">
                                              <button
                                                onClick={() => toggleDropdown(idx)}
                                                className="text-gray-700 bg-gray-200 p-2 rounded hover:bg-gray-300 cursor-pointer"
                                              >
                                                <EllipsisVertical className="h-4 w-4" />
                                              </button>
                                              {dropdownVisible === idx && (
                                                <div className="absolute right-0 mt-2 min-w-[200px] whitespace-nowrap bg-white shadow-lg z-50 rounded-md overflow-hidden">
                                                  {extraActions.map((action, index) => (
                                                  <button
                                                    key={index}
                                                    onClick={async () => {
                                                      if (action.confirmMessage) {
                                                        const proceed =
                                                          await confirmAction({
                                                            title: 'Are you sure?',
                                                            text: action.confirmMessage,
                                                            icon: 'warning',
                                                            confirmButtonText:
                                                              'Yes',
                                                            cancelButtonText: 'No',
                                                          });

                                                        if (proceed) {
                                                          action.onClick(row);
                                                        }
                                                      } else {
                                                        action.onClick(row);
                                                      }
                                                      closeDropdown();
                                                    }}
                                                    className={`flex items-center px-4 py-2 hover:bg-gray-100 w-full text-left cursor-pointer text-nowrap ${action.className ||
                                                      'text-gray-700'
                                                      }`}
                                                  >
                                                    {action.icon && (
                                                      <span className="mr-2 flex-shrink-0">
                                                        {action.icon}
                                                      </span>
                                                    )}
                                                    <span className="truncate">
                                                      {action.label}
                                                    </span>
                                                  </button>
                                                ))}
                                              </div>
                                            )}
                                            </div>
                                          );
                                        })()}
                                      </>
                                    )}
                                  </div>
                                </td>
                              )}
                          </tr>
                          {/* Expanded Details Row */}
                          {shouldShowExpand && isExpanded && (
                            <tr className="bg-gray-50 dark:bg-gray-900">
                              <td
                                colSpan={columns.length + (actions ? 1 : 0) + 1}
                                className="px-4 py-4 border-b border-gray-300 dark:border-gray-700"
                              >
                                {expandedRowRenderer(row)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
      {!isLoading && data.length > 0 && allowPaging && (
        <Pagination
          currentPage={currentPage ?? 0}
          totalPages={totalPages ?? 0}
          onPageChange={onPageChange}
          pageSize={pageSize ?? 0}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};

export default Table;
